export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const KEY = "ceo-cmd:theme";
const THEME_CHANGE_EVENT = "themechange";
const MQ = "(prefers-color-scheme: dark)";

const CYCLE: ThemeMode[] = ["light", "dark", "system"];

function systemPrefersDark(): boolean {
  return window.matchMedia(MQ).matches;
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode;
}

function readStoredTheme(): ThemeMode {
  try {
    const t = localStorage.getItem(KEY);
    if (t === "light" || t === "dark" || t === "system") return t;
  } catch {
    /* ignore */
  }
  return "system";
}

function paint(resolved: ResolvedTheme): void {
  const dark = resolved === "dark";
  document.documentElement.classList.toggle("dark", dark);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", dark ? "#0a0a0a" : "#f7f6f3");
}

/** Stored preference (light / dark / system). */
export function getCurrentTheme(): ThemeMode {
  return readStoredTheme();
}

/** Effective appearance after resolving system. */
export function getResolvedTheme(): ResolvedTheme {
  return resolveTheme(readStoredTheme());
}

export function applyTheme(mode: ThemeMode): void {
  paint(resolveTheme(mode));
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

/** Cycle light → dark → system → light. */
export function cycleTheme(): ThemeMode {
  const current = readStoredTheme();
  const next = CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length]!;
  applyTheme(next);
  return next;
}

export function onThemeChange(handler: () => void): () => void {
  window.addEventListener(THEME_CHANGE_EVENT, handler);
  return () => window.removeEventListener(THEME_CHANGE_EVENT, handler);
}

/** Sync DOM from storage and react to OS scheme changes when preference is system. */
export function initTheme(): void {
  applyTheme(readStoredTheme());

  const mq = window.matchMedia(MQ);
  const onSchemeChange = () => {
    if (readStoredTheme() === "system") applyTheme("system");
  };
  mq.addEventListener("change", onSchemeChange);
}
